<template>
	<div class="message-record-page">
		<div class="x-stat">
			<a-row>
				<a-col :span="3">
					<a-statistic title="今天" :value="stat.todayCount" suffix="条" />
				</a-col>
				<a-col :span="3">
					<a-statistic title="昨天" :value="stat.yesterdayCount" suffix="条" />
				</a-col>
				<a-col :span="3">
					<a-statistic title="近7平均" :value="stat.sevenDayAvg" suffix="条" />
				</a-col>
			</a-row>
		</div>

		<div class="record-panels">
			<a-card class="list-panel" :bordered="true" :body-style="{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }">
				<template slot="title">消息列表</template>
				<a-button slot="extra" type="primary" size="small" :loading="listLoading" @click="refreshList">刷新</a-button>

				<div class="list-body">
					<div class="message-list" ref="messageList" @scroll="onListScroll">
						<div
							v-for="item in messageList"
							:key="item.id"
							class="message-item"
							:class="{ active: selectedId === item.id }"
							@click="selectMessage(item)"
						>
							<div class="avatar-wrap">
								<div v-if="isSystemSender(item)" class="avatar avatar-bell" title="系统通知">
									<a-icon type="bell" />
								</div>
								<img v-else :src="item.fromUserAvatarUrl || ''" class="avatar" />
								<span v-if="item.isOnline === 'Y'" class="online-badge" title="在线"></span>
							</div>
							<div class="item-main">
								<div class="item-top">
									<span class="nickname">{{ isSystemSender(item) ? (item.fromUserNickname || '系统通知') : (item.fromUserNickname || '-') }}</span>
									<span class="time">{{ item.createdText || item.created || '' }}</span>
								</div>
								<div class="item-preview">
									<template v-if="item.type === 'PHOTO' || item.type === 'IMAGE'">
										<x-image
											v-if="getExtraValue(item.extra, 'imageUrl')"
											:src="getExtraValue(item.extra, 'imageUrl')"
											:width="56"
											:height="56"
											@click.native.stop
										/>
										<span v-else>[图片]</span>
									</template>
									<template v-else-if="item.type === 'VOICE'">
										<div @click.stop>
											<voice-message
												v-if="getExtraValue(item.extra, 'voiceUrl')"
												:msg-item="{
													content: getExtraValue(item.extra, 'voiceUrl'),
													duration: getExtraValue(item.extra, 'duration') || 0,
													isRead: 'Y',
												}"
												:is-self="false"
											/>
											<span v-else>[语音]</span>
										</div>
									</template>
									<template v-else-if="item.type === 'VIDEO'">
										<div @click.stop>
											<video-modal-player
												v-if="getExtraValue(item.extra, 'videoUrl')"
												:video-src="getExtraValue(item.extra, 'videoUrl')"
												:thumbnail="getExtraValue(item.extra, 'coverUrl') || getExtraValue(item.extra, 'videoUrl')"
												video-title="视频"
											/>
											<span v-else>[视频]</span>
										</div>
									</template>
									<span v-else-if="item.type === 'GIFT'">[礼物]</span>
									<span v-else-if="item.type === 'VOLTE' || item.type === 'VOLET'">{{ getCallPreview(item, '语音通话') }}</span>
									<span v-else-if="item.type === 'VILTE' || item.type === 'VILET'">{{ getCallPreview(item, '视频通话') }}</span>
									<span v-else>{{ item.content || '' }}</span>
								</div>
							</div>
						</div>

						<div class="list-footer">
							<span v-if="listLoading">加载中...</span>
							<span v-else-if="!hasMore && messageList.length">没有更多了</span>
							<span v-else-if="!messageList.length && !listLoading">暂无消息</span>
						</div>
					</div>

					<div
						v-show="showListBackTop"
						class="list-back-top"
						title="回到顶部"
						@click="scrollListToTop"
					>
						<a-icon type="to-top" />
					</div>
				</div>
			</a-card>
		</div>

		<conversation-message-drawer ref="conversationDrawer" />
	</div>
</template>

<script>
	import { messageList, messageStat } from '@/api/op/message';
	import ConversationMessageDrawer from '@/components/ConversationMessageDrawer.vue';
	import XImage from '@/components/XImage.vue';
	import VoiceMessage from '@/components/VoiceMessage.vue';
	import VideoModalPlayer from '@/components/VideoModalPlayer.vue';

	const PAGE_SIZE = 20;

	export default {
		name: 'MessageList',
		components: {
			ConversationMessageDrawer,
			XImage,
			VoiceMessage,
			VideoModalPlayer,
		},
		data() {
			return {
				stat: {
					todayCount: 0,
					yesterdayCount: 0,
					sevenDayAvg: 0,
				},
				messageList: [],
				listLoading: false,
				pageIndex: 1,
				totalRow: 0,
				hasMore: true,
				selectedId: null,
				showListBackTop: false,
			};
		},
		created() {
			this.fetchStat();
			this.fetchList(true);
		},
		methods: {
			parseExtra(extra) {
				if (extra == null || String(extra).trim() === '') return {};
				if (typeof extra === 'object' && !Array.isArray(extra)) return extra;
				try {
					const parsed = JSON.parse(extra);
					return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
				} catch (e) {
					return {};
				}
			},
			getExtraValue(extra, key) {
				const obj = this.parseExtra(extra);
				const val = obj[key];
				return val == null ? '' : val;
			},
			formatCallDuration(seconds) {
				const sec = Number(seconds);
				if (!Number.isFinite(sec) || sec <= 0) return '';
				const mins = Math.floor(sec / 60);
				const secs = Math.floor(sec % 60);
				return `${mins}:${String(secs).padStart(2, '0')}`;
			},
			getHangupText(hangup) {
				const map = {
					1: '被叫拒绝',
					2: '被叫未接听',
					3: '主叫取消',
					4: '金币不足挂断',
					5: '违规中断',
				};
				return map[Number(hangup)] || '';
			},
			getCallPreview(item, label) {
				const callLen = this.getExtraValue(item.extra, 'callLen');
				const durationText = this.formatCallDuration(callLen);
				if (durationText) {
					return `[${label}] ${durationText}`;
				}
				const hangupText = this.getHangupText(this.getExtraValue(item.extra, 'hangup'));
				return hangupText ? `[${label}] ${hangupText}` : `[${label}]`;
			},
			isSystemSender(item) {
				const id = item && item.fromUserId;
				return id === 0 || id === '0';
			},
			fetchStat() {
				messageStat().then((res) => {
					if (!res.isSucceed) return;
					this.stat = {
						todayCount: Number(res.data.todayCount) || 0,
						yesterdayCount: Number(res.data.yesterdayCount) || 0,
						sevenDayAvg: Number(res.data.sevenDayAvg) || 0,
					};
				}).catch(() => {});
			},
			refreshList() {
				this.fetchStat();
				this.fetchList(true);
			},
			fetchList(reset) {
				if (this.listLoading) return;
				if (!reset && !this.hasMore) return;

				if (reset) {
					this.pageIndex = 1;
					this.hasMore = true;
				}

				this.listLoading = true;
				messageList({
					page: this.pageIndex,
					pageSize: PAGE_SIZE,
				}).then((res) => {
					if (!res.isSucceed) {
						this.$message.error(res.message || '加载失败');
						return;
					}
					const list = (res.data && res.data.recordList) || [];
					this.totalRow = Number(res.data.totalRow) || 0;
					this.messageList = reset ? list : this.messageList.concat(list);
					this.hasMore = this.messageList.length < this.totalRow && list.length >= PAGE_SIZE;
					if (list.length) {
						this.pageIndex += 1;
					} else {
						this.hasMore = false;
					}
				}).catch(() => {
					this.$message.error('加载失败');
				}).finally(() => {
					this.listLoading = false;
				});
			},
			onListScroll(e) {
				const el = e.target;
				if (!el) return;
				this.showListBackTop = el.scrollTop > 200;
				if (this.listLoading || !this.hasMore) return;
				if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
					this.fetchList(false);
				}
			},
			scrollListToTop() {
				const el = this.$refs.messageList;
				if (!el) return;
				if (typeof el.scrollTo === 'function') {
					el.scrollTo({ top: 0, behavior: 'smooth' });
				} else {
					el.scrollTop = 0;
				}
				this.showListBackTop = false;
			},
			selectMessage(item) {
				const nextConversationId = Number(item.conversationId) || 0;
				const nextMessageId = Number(item.id) || 0;
				if (!nextConversationId || !nextMessageId) {
					this.$message.warning('暂无会话记录');
					return;
				}
				this.selectedId = nextMessageId;
				// 系统消息点到的是会话对方，普通消息点到的是发送方
				const mainUserId = this.isSystemSender(item)
					? (Number(item.toUserId) || 0)
					: (Number(item.fromUserId) || 0);
				this.$refs.conversationDrawer && this.$refs.conversationDrawer.open({
					conversationId: nextConversationId,
					anchorMessageId: nextMessageId,
					mainUserId,
				});
			},
		},
	};
</script>

<style lang="less" scoped>
	.message-record-page {
		/* 顶栏约 50px + PageView 上边距 16px + 底部间距 20px；负边距抵消布局 60px 占位 */
		height: calc(100vh - 88px);
		max-height: calc(100vh - 88px);
		margin-bottom: -60px;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		box-sizing: border-box;
	}

	.message-record-page ::v-deep .x-stat {
		flex-shrink: 0;
		margin-bottom: 8px;
		padding-top: 8px;
		padding-bottom: 8px;
	}

	.record-panels {
		display: flex;
		align-items: stretch;
		flex: 1;
		min-height: 0;
		margin-top: 0;
	}

	.list-panel {
		width: 760px;
		max-width: 100%;
		flex: 0 0 760px;
		height: 100%;
		display: flex;
		flex-direction: column;

		::v-deep .ant-card-body {
			flex: 1;
			min-height: 0;
			display: flex;
			flex-direction: column;
		}
	}

	.list-body {
		position: relative;
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
	}

	.message-list {
		flex: 1;
		overflow-y: auto;
		padding: 8px 0;
		min-height: 0;
	}

	.list-back-top {
		position: absolute;
		right: 16px;
		bottom: 24px;
		width: 40px;
		height: 40px;
		border-radius: 50%;
		background: #1890ff;
		color: #fff;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
		z-index: 5;
		transition: opacity 0.2s, transform 0.2s;

		&:hover {
			background: #40a9ff;
			transform: translateY(-2px);
		}
	}

	.message-item {
		display: flex;
		padding: 12px 16px;
		cursor: pointer;
		border-bottom: 1px solid #f0f0f0;
		transition: background 0.2s;

		&:hover,
		&.active {
			background: #e6f7ff;
		}
	}

	.avatar-wrap {
		position: relative;
		width: 48px;
		height: 48px;
		margin-right: 12px;
		flex-shrink: 0;
	}

	.avatar {
		width: 48px;
		height: 48px;
		border-radius: 4px;
		object-fit: cover;
		background: #f5f5f5;
	}

	.avatar-bell {
		display: flex;
		align-items: center;
		justify-content: center;
		background: #fff7e6;
		color: #fa8c16;
		font-size: 22px;
	}

	.online-badge {
		position: absolute;
		right: -1px;
		bottom: -1px;
		width: 12px;
		height: 12px;
		background: #52c41a;
		border: 2px solid #fff;
		border-radius: 50%;
		box-shadow: 0 0 0 1px rgba(82, 196, 26, 0.2);
	}

	.item-main {
		flex: 1;
		min-width: 0;
	}

	.item-top {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 6px;
	}

	.nickname {
		font-weight: 600;
		color: #262626;
	}

	.time {
		color: #8c8c8c;
		font-size: 12px;
		margin-left: 8px;
		flex-shrink: 0;
	}

	.item-preview {
		color: #595959;
		font-size: 13px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;

		::v-deep .x-image,
		::v-deep .cover-wrap,
		::v-deep .voice-content {
			display: inline-block;
			vertical-align: middle;
		}

		::v-deep .cover-img {
			width: 56px !important;
			height: 56px !important;
			object-fit: cover;
			border-radius: 4px;
		}

		::v-deep .cover-play-btn {
			transform: scale(0.7);
		}
	}

	.list-footer {
		text-align: center;
		color: #8c8c8c;
		font-size: 12px;
		padding: 12px 0 16px;
	}
</style>
